<template>
  <v-footer
    class="text-center bg-primary-darken-1 justify-space-evenly footer mt-10"
    :class="{ menu_open: !isRailMode && !mobile }"
  >
    <v-row class="mb-0 pt-6 pb-3">
      <v-col
        cols="12"
        sm="4"
        class="d-flex flex-column justify-center align-center"
      >
        <div class="d-flex flex-column">
          <v-btn
            v-if="getSetting('terms_and_conditions_enabled')"
            variant="text"
            to="/policies/termsandconditions"
            :text="t('policies.terms')"
            class="d-flex justify-start"
          />
          <v-btn
            v-if="getSetting('privacy_policy_enabled')"
            variant="text"
            to="/policies/privacy"
            class="d-flex justify-start"
            :text="t('policies.privacy')"
          />
          <v-btn
            v-if="getSetting('cookie_consent_enabled')"
            variant="text"
            to="/policies/cookies"
            class="d-flex justify-start"
            :text="t('policies.cookie')"
          />
          <language-switcher
            class="d-flex justify-start"
          />
          <v-btn
            variant="text"
            class="d-flex justify-start"
            prepend-icon="fas fa-sign-out-alt"
            :text="t('login.log_out')"
            @click="handleLogout"
          />
        </div>
      </v-col>
      <v-col
        cols="12"
        sm="4"
      >
        <v-img
          :src="logoUrl"
          height="85px"
        />
        <div class="text-uppercase text-body-medium mt-1 text-white">
          &copy; {{ thisYear() }} - {{ getSetting('domain') }} - {{ version }}
        </div>
      </v-col>
      <v-col
        cols="12"
        sm="4"
        class="d-flex flex-column justify-center align-center"
      >
        <v-dialog
          v-model="dialog"
          :fullscreen="xs"
          :width="!xs ? 'auto' : undefined"
          :loading="loading"
          max-width="750"
          @update:model-value="onDialogVisibilityChange"
        >
          <template #activator="{ props: dialogProps }">
            <v-card
              rounded
              v-bind="dialogProps"
              width="200px"
            >
              <v-card-text class="pa-2 pt-3 bg-error text-uppercase text-body-large text-white">
                {{ t('dayoverseer.emergency_help') }}
              </v-card-text>
              <v-card-text class="pa-2 pt-3 text-uppercase text-body-large text-black">
                {{ t('dayoverseer.contact_information') }}
              </v-card-text>
            </v-card>
          </template>

          <template #default="{ isActive }">
            <global-card
              :title="t('dayoverseer.emergency_information')"
              closable
              @close="isActive.value = false"
            >
              <v-card
                v-if="shiftCaptainContact"
                variant="flat"
              >
                <v-row>
                  <v-col align="center">
                    <user-avatar :user="shiftCaptainContact" />
                  </v-col>
                  <v-col>
                    <div class="text-headline-medium">
                      {{ shiftCaptainContact.name }}
                    </div>
                    <div>
                      {{ getSetting('captain_label') }} {{ t('dayoverseer.contact') }}
                    </div>
                    <phone-number
                      class="mb-2"
                      :number="shiftCaptainContact.mobile_phone"
                    />
                  </v-col>
                </v-row>
              </v-card>

              <v-card
                v-for="dayOverseer in dayOverseerContacts"
                :key="dayOverseer.id"
                density="comfortable"
                variant="flat"
              >
                <v-card-text
                  class="d-flex align-center"
                  :class="{ 'flex-row': !xs, 'flex-column': xs }"
                >
                  <div :class="{ 'mr-6': !xs }">
                    <user-avatar :user="dayOverseer.user" />
                  </div>
                  <div class="d-flex flex-column align-center">
                    <div class="text-headline-small text-primary-darken-1 mb-1">
                      {{ dayOverseer.user.name }}
                    </div>

                    <div
                      v-if="dayOverseer.notes"
                      class="text-body-large text-font mb-1"
                    >
                      {{ dayOverseer.notes }}
                    </div>
                    <phone-number
                      :number="dayOverseer.user.mobile_phone"
                      class="mb-2"
                    />
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                v-if="emergencyFaq"
                variant="flat"
              >
                <v-card-title>
                  <div class="text-left text-wrap text-headline-small">
                    {{ emergencyFaq.question }}
                  </div>
                </v-card-title>
                <v-card-text>
                  <rich-text-content
                    :data="emergencyFaq.answer"
                  />
                </v-card-text>
              </v-card>
              <template #actions>
                <v-spacer />
                <v-btn
                  :text="t('actions.close')"
                  color="dark-grey"
                  @click="isActive.value = false"
                />
              </template>
            </global-card>
          </template>
        </v-dialog>
      </v-col>
    </v-row>
  </v-footer>
</template>

<script setup lang="ts">
import axios from 'axios';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import RichTextContent from '@/components/rich-text/RichTextContent.vue';
import { useDate, useSettings } from '@/composables/index.ts';
import { useMenu } from '@/composables/useMenu';
import { useErrorStore } from '@/stores/errors';
import UserAvatar from './images/UserAvatar.vue';

defineProps<{
  version?: string;
}>();
const { t } = useI18n();
const { getSetting, logoUrl } = useSettings();
const { thisYear } = useDate();
const { isRailMode } = useMenu();
const { mobile, xs } = useDisplay();
const { handleError } = useErrorStore();

const dialog = ref(false);
const loading = ref(false);
const shiftCaptainContact = ref<App.Models.User>();
const dayOverseerContacts = ref<Array<App.Models.DayOverseer>>([]);
const emergencyFaq = ref<App.Models.Faq>();

async function loadFooterData() {
  if (dayOverseerContacts.value.length) {
    return;
  }
  try {
    loading.value = true;
    const response = await axios.get('/api/menu/footer');
    shiftCaptainContact.value = response.data.data.shiftCaptainContact;
    dayOverseerContacts.value = response.data.data.dayOverseerContacts;
    emergencyFaq.value = response.data.data.emergencyFaq;
  }
  catch (e) {
    handleError(e, t('errors.cannot_load_widgets'));
  }
  finally {
    loading.value = false;
  }
}

function onDialogVisibilityChange(visible: boolean) {
  if (visible) {
    loadFooterData();
  }
}

function handleLogout(event: KeyboardEvent) {
  event.preventDefault();
  const form = document.getElementById('logout-form') as HTMLFormElement;
  form.submit();
}
</script>

<style scoped>
.footer {
  transition: margin 0.2s;
  margin-left: 0px;
}

.footer.menu_open {
  transition: margin 0.2s;
  margin-left: 250px;
}
</style>
